import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";

export default function Checkout() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const tierCode = params.get("tier");

  const [item, setItem] = useState(null);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    shipping_address: "",
    size_code: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get(`/drop/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => nav("/"));
  }, [id, nav]);

  const activeTier = item?.tiers.find((t) => t.code === tierCode) || item?.tiers[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@") || !form.full_name || !form.shipping_address || !form.size_code) {
      toast.error("ALL FIELDS REQUIRED");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/mock", {
        item_id: item.id,
        tier_code: activeTier.code,
        size_code: form.size_code.toUpperCase(),
        email: form.email,
        full_name: form.full_name,
        shipping_address: form.shipping_address,
      });
      nav(`/success/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CHECKOUT FAILED");
    } finally {
      setLoading(false);
    }
  };

  if (!item || !activeTier) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex h-[60vh] items-center justify-center font-mono-stamp text-xs text-[#a0a0a0]">
          LOADING…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen">
      <Navbar />
      <section className="mx-auto max-w-[1400px] px-6 pb-24 pt-28 md:px-10">
        <p className="font-mono-stamp text-xs text-[#a0a0a0]">
          ▸ MOCK CHECKOUT — NO REAL CARD NEEDED
        </p>
        <h1 className="font-display mt-4 text-6xl leading-[0.88] md:text-8xl">
          CHECKOUT.
        </h1>

        <div className="mt-12 grid grid-cols-12 gap-10">
          <form
            onSubmit={submit}
            className="col-span-12 space-y-6 md:col-span-7"
          >
            <div>
              <label className="font-mono-stamp text-[10px] text-[#a0a0a0]">
                ▸ EMAIL
              </label>
              <input
                data-testid="co-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full border-b-2 border-[#444] bg-transparent py-3 font-mono-stamp text-base text-white placeholder:text-[#555] focus:border-white focus:outline-none"
                placeholder="YOU@EMAIL.COM"
              />
            </div>

            <div>
              <label className="font-mono-stamp text-[10px] text-[#a0a0a0]">
                ▸ FULL NAME
              </label>
              <input
                data-testid="co-name"
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-2 w-full border-b-2 border-[#444] bg-transparent py-3 font-mono-stamp text-base text-white placeholder:text-[#555] focus:border-white focus:outline-none"
                placeholder="FIRST LAST"
              />
            </div>

            <div>
              <label className="font-mono-stamp text-[10px] text-[#a0a0a0]">
                ▸ SHIPPING ADDRESS
              </label>
              <textarea
                data-testid="co-address"
                value={form.shipping_address}
                onChange={(e) =>
                  setForm({ ...form, shipping_address: e.target.value })
                }
                rows={3}
                className="mt-2 w-full resize-none border-2 border-[#444] bg-transparent p-3 font-mono-stamp text-base text-white placeholder:text-[#555] focus:border-white focus:outline-none"
                placeholder="STREET, CITY, POSTCODE, COUNTRY"
              />
            </div>

            <div>
              <label className="font-mono-stamp text-[10px] text-[#a0a0a0]">
                ▸ BAS1C SIZE CODE
              </label>
              <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
                <input
                  data-testid="co-size"
                  type="text"
                  value={form.size_code}
                  onChange={(e) =>
                    setForm({ ...form, size_code: e.target.value })
                  }
                  placeholder="B-04-M"
                  className="flex-1 border-b-2 border-[#444] bg-transparent py-3 font-mono-stamp text-base uppercase text-white placeholder:text-[#555] focus:border-white focus:outline-none"
                />
                <Link
                  to="/find-your-fit"
                  className="font-mono-stamp text-[11px] text-[#a0a0a0] underline underline-offset-4 hover:text-white"
                >
                  FIND YOUR FIT →
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="co-submit"
              className="btn-brutal btn-brutal-solid w-full justify-center disabled:opacity-50"
            >
              {loading
                ? "PLACING ORDER…"
                : `PLACE MOCK ORDER — $${activeTier.price} →`}
            </button>

            <p className="font-mono-stamp text-[10px] text-[#555]">
              * NO PAYMENT TAKEN. THIS IS A DEMO CHECKOUT. YOUR RUN NUMBER
              WILL BE STAMPED INSTANTLY.
            </p>
          </form>

          {/* Summary */}
          <aside className="col-span-12 md:col-span-5">
            <div className="border border-[#333] bg-[#0a0a0a] p-6">
              <p className="font-mono-stamp text-[10px] text-[#a0a0a0]">
                ▸ ORDER SUMMARY
              </p>
              <div className="mt-4 flex gap-4">
                <img
                  src={item.image}
                  alt={item.name}
                  className="h-28 w-24 border border-[#333] object-cover"
                />
                <div className="flex-1">
                  <p className="font-display text-3xl">
                    {item.name.toUpperCase()}
                  </p>
                  <p className="mt-1 font-mono-stamp text-[11px] text-[#a0a0a0]">
                    DESIGN {item.design_no} · TIER {activeTier.code}
                  </p>
                  <p className="mt-1 font-mono-stamp text-[11px] text-[#a0a0a0]">
                    {activeTier.fabric} / {activeTier.origin}
                  </p>
                </div>
              </div>
              <div className="mt-6 border-t border-[#222] pt-4">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono-stamp text-xs text-[#a0a0a0]">
                    SUBTOTAL
                  </span>
                  <span className="font-display text-3xl">
                    ${activeTier.price}
                  </span>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="font-mono-stamp text-xs text-[#a0a0a0]">
                    SHIPPING
                  </span>
                  <span className="font-mono-stamp text-xs">INCLUDED</span>
                </div>
                <div className="mt-4 flex items-baseline justify-between border-t border-[#222] pt-3">
                  <span className="font-mono-stamp text-xs text-white">
                    TOTAL
                  </span>
                  <span className="font-display text-4xl">
                    ${activeTier.price}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
